import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, ArrowUpDown, Columns3, Package, Search } from 'lucide-react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';

import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CountryLabel } from '@/components/shared/CountryLabel';
import { StatusPill, type StatusTone } from '@/components/shared/StatusPill';
import { TableSkeleton } from '@/components/shared/TableSkeleton';
import { getMyTenant, TENANT_ME_QUERY_KEY } from '@/lib/api/tenants';
import type { BcConnection } from '@/lib/api/connections';
import { syncStoreOrders } from '@/lib/api/stores';
import { getSyncJob, isTerminalSyncJobStatus, SYNC_JOB_QUERY_KEY } from '@/lib/api/sync-jobs';
import { listOrders, ORDERS_QUERY_KEY, type Order } from '@/lib/api/orders';
import { formatDate } from '@/lib/format';

import { SyncJobModal } from './SyncJobModal';

const ACTIVE_JOB_STORAGE_KEY = 'mailchute.active-sync-job-id';
const COLUMN_VISIBILITY_STORAGE_KEY = 'mailchute.orders-column-visibility';

type StatusFilter = 'all' | 'waiting' | 'shipped' | 'cancelled';

const WAITING_STATUS_IDS = new Set([11, 9]);
const SHIPPED_STATUS_IDS = new Set([2, 3, 10]);
const CANCELLED_STATUS_IDS = new Set([4, 5, 6, 14]);

/**
 * Default sort: newest order first by `bc_date_created` desc, with
 * `bc_order_id` desc as a stable tiebreaker (BC IDs are monotonically
 * increasing within a store, so the higher id of two same-timestamp
 * orders is the more recent). Encoded as a custom sortingFn on the
 * date column so a click on another column still uses TanStack's
 * built-in single-column sort.
 */
const DEFAULT_SORTING: SortingState = [{ id: 'bc_date_created', desc: true }];

/**
 * Hideable column ids — every column except `order_number`, which is
 * the link to the detail view and must stay visible. Used both for the
 * dropdown menu and the persisted-visibility loader so a corrupt
 * storage value never hides the order-number link.
 */
const HIDEABLE_COLUMN_IDS = [
  'customer_name',
  'destination',
  'service',
  'bc_date_created',
  'bc_status_id',
  'source',
] as const;

const COLUMN_HEADER_LABELS: Record<(typeof HIDEABLE_COLUMN_IDS)[number], string> = {
  customer_name: 'Customer',
  destination: 'Destination',
  service: 'Service',
  bc_date_created: 'Placed',
  bc_status_id: 'Status',
  source: 'Channel',
};

/**
 * Read the persisted column-visibility map. Defensive against missing
 * keys (default to visible) and corrupt JSON (default to all visible).
 * SSR-safe (no localStorage on the server) — the dashboard is SPA-only
 * but the helper is run during initial state, before render.
 */
function loadColumnVisibility(): VisibilityState {
  if (typeof window === 'undefined') return {};
  try {
    const raw = globalThis.localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: VisibilityState = {};
    for (const id of HIDEABLE_COLUMN_IDS) {
      const value = (parsed as Record<string, unknown>)[id];
      if (typeof value === 'boolean') out[id] = value;
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Orders list — the dashboard's primary surface. Renders a TanStack
 * Table of the tenant's orders (newest-modified first) filtered by a
 * free-text search and a status bucket. Each row's order number links
 * to the detail view. The "Sync now" header action opens the async
 * progress modal (ADR 0019). Deferred column scope (destination,
 * service, customizable columns) is captured in ADR 0020.
 */
export function OrdersPage() {
  const tenantQuery = useQuery({
    queryKey: TENANT_ME_QUERY_KEY,
    queryFn: async () => {
      const result = await getMyTenant();
      if (result.isErr()) throw new Error(result.error.message);
      return result.value;
    },
  });
  const connection = tenantQuery.data?.bc_connection;

  const ordersQuery = useQuery({
    queryKey: ORDERS_QUERY_KEY,
    queryFn: async () => {
      const result = await listOrders();
      if (result.isErr()) throw new Error(result.error.message);
      return result.value.orders;
    },
  });
  const orders = ordersQuery.data;

  // `activeJobId` tracks the in-flight sync job for the lifetime of the
  // poll (set on Sync-now or on re-attach; cleared when the job reaches
  // terminal status). `modalVisible` is independent — the user can
  // close the modal mid-sync; polling + cache invalidation continue per
  // [ADR 0021](../../../../docs/adr/0021-frontend-feedback-loop-polish.md).
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  // Sort state lives in the page (not OrdersTable) so it survives a
  // filter-driven empty render — TanStack would otherwise drop it.
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  // Persisted column visibility (ADR 0020 Slice B). Hydrated lazily from
  // localStorage on mount; pushed back on every change.
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(loadColumnVisibility);
  useEffect(() => {
    globalThis.localStorage.setItem(
      COLUMN_VISIBILITY_STORAGE_KEY,
      JSON.stringify(columnVisibility),
    );
  }, [columnVisibility]);
  const queryClient = useQueryClient();

  const filteredOrders = useMemo(
    () => (orders ? filterOrders(orders, search, statusFilter) : []),
    [orders, search, statusFilter],
  );

  const syncJobQuery = useQuery({
    queryKey: SYNC_JOB_QUERY_KEY(activeJobId ?? '__none__'),
    enabled: activeJobId !== null,
    queryFn: async () => {
      if (!activeJobId) throw new Error('no activeJobId');
      const result = await getSyncJob(activeJobId);
      if (result.isErr()) throw new Error(result.error.message);
      return result.value;
    },
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      if (!status) return 2000;
      return isTerminalSyncJobStatus(status) ? false : 2000;
    },
  });
  const syncJob = syncJobQuery.data;

  // When the in-flight job reaches terminal status, refresh the orders
  // table so newly-synced rows appear without a manual reload. We
  // refresh on `completed` AND `failed` — a failed run may still have
  // enqueued some orders before failing, so the table can change either
  // way. `activeJobId` intentionally stays set so the modal keeps
  // displaying the terminal state until the user closes it; a fresh
  // Sync now click replaces it.
  useEffect(() => {
    if (syncJob && isTerminalSyncJobStatus(syncJob.status)) {
      void queryClient.invalidateQueries({ queryKey: ORDERS_QUERY_KEY });
      globalThis.localStorage.removeItem(ACTIVE_JOB_STORAGE_KEY);
    }
  }, [syncJob, queryClient]);

  useEffect(() => {
    const stored = globalThis.localStorage.getItem(ACTIVE_JOB_STORAGE_KEY);
    if (!stored) return;
    void (async () => {
      const result = await getSyncJob(stored);
      if (result.isOk() && !isTerminalSyncJobStatus(result.value.status)) {
        setActiveJobId(stored);
        setModalVisible(true);
      } else {
        globalThis.localStorage.removeItem(ACTIVE_JOB_STORAGE_KEY);
      }
    })();
  }, []);

  function handleJobStarted(jobId: string) {
    globalThis.localStorage.setItem(ACTIVE_JOB_STORAGE_KEY, jobId);
    setActiveJobId(jobId);
    setModalVisible(true);
  }

  function handleClose() {
    setModalVisible(false);
  }

  function clearFilters() {
    setSearch('');
    setStatusFilter('all');
  }

  const hasAnyOrders = orders && orders.length > 0;
  // Disable Sync now while a job is genuinely in-flight; re-enable as
  // soon as the terminal status arrives so the merchant doesn't have
  // to close the modal first.
  const syncInFlight = syncJob ? !isTerminalSyncJobStatus(syncJob.status) : activeJobId !== null;

  return (
    <div>
      <PageHeader
        title="Orders"
        subtitle="Orders waiting to ship"
        actions={
          connection ? (
            <SyncNowButton
              connection={connection}
              disabled={syncInFlight}
              onJobStarted={handleJobStarted}
            />
          ) : undefined
        }
      />
      <SyncJobModal open={modalVisible} job={syncJob} onClose={handleClose} />
      {ordersQuery.isLoading ? (
        <TableSkeleton columns={ORDERS_COLUMN_LABELS} />
      ) : hasAnyOrders ? (
        <div className="space-y-4">
          <FilterBar
            search={search}
            onSearchChange={setSearch}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
          />
          {filteredOrders.length > 0 ? (
            <OrdersTable
              orders={filteredOrders}
              sorting={sorting}
              onSortingChange={setSorting}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
            />
          ) : (
            <NoMatchingOrders onClear={clearFilters} />
          )}
        </div>
      ) : (
        <OrdersEmptyState />
      )}
    </div>
  );
}

/** Mirrors the real OrdersTable header order — kept in sync by hand. */
const ORDERS_COLUMN_LABELS = [
  'Order #',
  'Customer',
  'Destination',
  'Service',
  'Placed',
  'Status',
  'Channel',
] as const;

function FilterBar({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  columnVisibility,
  onColumnVisibilityChange,
}: {
  search: string;
  onSearchChange: (next: string) => void;
  statusFilter: StatusFilter;
  onStatusFilterChange: (next: StatusFilter) => void;
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (next: VisibilityState) => void;
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="relative flex-1 sm:max-w-sm">
        <Search
          className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2"
          aria-hidden="true"
        />
        <Input
          type="search"
          value={search}
          onChange={(event) => {
            onSearchChange(event.currentTarget.value);
          }}
          placeholder="Search orders…"
          aria-label="Search orders"
          className="pl-8"
        />
      </div>
      <Select
        value={statusFilter}
        onValueChange={(next) => {
          onStatusFilterChange(next as StatusFilter);
        }}
      >
        <SelectTrigger className="w-full sm:w-[200px]" aria-label="Filter by status">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          <SelectItem value="waiting">Waiting to ship</SelectItem>
          <SelectItem value="shipped">Shipped</SelectItem>
          <SelectItem value="cancelled">Cancelled / refunded</SelectItem>
        </SelectContent>
      </Select>
      <ColumnsMenu
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={onColumnVisibilityChange}
      />
    </div>
  );
}

function ColumnsMenu({
  columnVisibility,
  onColumnVisibilityChange,
}: {
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (next: VisibilityState) => void;
}) {
  function toggle(id: (typeof HIDEABLE_COLUMN_IDS)[number], next: boolean) {
    onColumnVisibilityChange({ ...columnVisibility, [id]: next });
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="w-full sm:w-auto" aria-label="Show or hide columns">
          <Columns3 className="size-4" aria-hidden="true" />
          Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>Show columns</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {HIDEABLE_COLUMN_IDS.map((id) => (
          <DropdownMenuCheckboxItem
            key={id}
            checked={columnVisibility[id] !== false}
            onCheckedChange={(next) => {
              toggle(id, next);
            }}
            // Prevent the menu from closing on each click — merchants
            // usually toggle several at once.
            onSelect={(e) => {
              e.preventDefault();
            }}
          >
            {COLUMN_HEADER_LABELS[id]}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const columnHelper = createColumnHelper<Order>();

function OrdersTable({
  orders,
  sorting,
  onSortingChange,
  columnVisibility,
  onColumnVisibilityChange,
}: {
  orders: readonly Order[];
  sorting: SortingState;
  // React setters — same shape as TanStack's onChange handlers, so we
  // pass them straight through without wrapper functions.
  onSortingChange: Dispatch<SetStateAction<SortingState>>;
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: Dispatch<SetStateAction<VisibilityState>>;
}) {
  const table = useReactTable({
    data: orders as Order[],
    columns: [
      columnHelper.accessor('bc_order_id', {
        id: 'order_number',
        header: 'Order #',
        cell: (info) => (
          <Link
            to={`/app/orders/${encodeURIComponent(info.row.original.order_id)}`}
            className="text-primary font-medium tabular-nums hover:underline"
          >
            #{info.row.original.bc_order_id}
          </Link>
        ),
        // BC ids are numeric; the default alphanumeric sort would mis-order
        // 100 vs 99. Sort as numbers.
        sortingFn: (a, b) => a.original.bc_order_id - b.original.bc_order_id,
        enableHiding: false,
      }),
      columnHelper.accessor('customer_name', {
        id: 'customer_name',
        header: 'Customer',
        cell: (info) => info.row.original.customer_name ?? '—',
        sortingFn: (a, b) => compareStrings(a.original.customer_name, b.original.customer_name),
      }),
      // Accessor columns (not display) so TanStack's sort machinery
      // sees them as sortable. The accessorFn is called as part of the
      // sortingFn's getValue path, so the helper isn't an uncovered
      // function despite the cell renderer reading from row.original.
      columnHelper.accessor((row) => row.ship_to?.country ?? '', {
        id: 'destination',
        header: 'Destination',
        cell: (info) => <CountryLabel code={info.row.original.ship_to?.country} />,
        sortingFn: (a, b) =>
          compareStrings(a.original.ship_to?.country, b.original.ship_to?.country),
      }),
      columnHelper.accessor((row) => row.customer_shipping_method ?? '', {
        id: 'service',
        header: 'Service',
        cell: (info) => info.row.original.customer_shipping_method ?? '—',
        sortingFn: (a, b) =>
          compareStrings(a.original.customer_shipping_method, b.original.customer_shipping_method),
      }),
      columnHelper.accessor('bc_date_created', {
        id: 'bc_date_created',
        header: 'Placed',
        cell: (info) => formatDate(info.row.original.bc_date_created),
        // ISO-8601 strings sort lexicographically === chronologically.
        // Tiebreaker on the equal-timestamp case uses bc_order_id (BC
        // IDs grow monotonically within a store), satisfying the
        // "date first, order id otherwise" default.
        sortingFn: (a, b) => {
          const cmp = a.original.bc_date_created.localeCompare(b.original.bc_date_created);
          if (cmp !== 0) return cmp;
          return a.original.bc_order_id - b.original.bc_order_id;
        },
      }),
      columnHelper.accessor('bc_status_id', {
        id: 'bc_status_id',
        header: 'Status',
        cell: (info) => (
          <StatusPill tone={toneForBcStatus(info.row.original.bc_status_id)}>
            {info.row.original.bc_status}
          </StatusPill>
        ),
        // Sort by the visible label, not the numeric id — id ordering
        // is BC-internal and meaningless to the merchant.
        sortingFn: (a, b) => a.original.bc_status.localeCompare(b.original.bc_status),
      }),
      columnHelper.accessor('source', {
        id: 'source',
        header: 'Channel',
        cell: (info) => channelLabel(info.row.original.source),
        // Single-channel at MVP — sorting it is meaningless until the
        // second source lands. Keeps the non-sortable header path
        // exercised, which the rest of the columns don't reach.
        enableSorting: false,
      }),
    ],
    state: { sorting, columnVisibility },
    onSortingChange,
    onColumnVisibilityChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    // Wrapper scrolls horizontally when the table is wider than the
    // viewport (rather than wrapping cell content vertically), which
    // keeps each row a single visual line — easier to scan a long order
    // list. Cells / headers carry `whitespace-nowrap` so long values
    // (customer names, shipping methods) widen the column instead of
    // breaking onto a second line.
    <div className="border-border bg-card overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 border-border border-b">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers
                .filter((h) => h.column.getIsVisible())
                .map((header) => {
                  const canSort = header.column.getCanSort();
                  const sortDir = header.column.getIsSorted();
                  const labelText = String(header.column.columnDef.header ?? '');
                  return (
                    <th
                      key={header.id}
                      className="text-muted-foreground whitespace-nowrap px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide"
                    >
                      {canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="hover:text-foreground inline-flex items-center gap-1.5 transition-colors"
                          aria-label={`Sort by ${labelText}`}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <SortIndicator direction={sortDir === false ? null : sortDir} />
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className="border-border hover:bg-muted/30 border-b transition-colors last:border-b-0"
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="text-foreground whitespace-nowrap px-4 py-3">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SortIndicator({ direction }: { direction: 'asc' | 'desc' | null }) {
  if (direction === 'asc') return <ArrowUp className="size-3" aria-hidden="true" />;
  if (direction === 'desc') return <ArrowDown className="size-3" aria-hidden="true" />;
  return <ArrowUpDown className="text-muted-foreground/50 size-3" aria-hidden="true" />;
}

/**
 * String comparison that pushes undefined / empty to the end regardless
 * of sort direction. TanStack flips the result for descending sort, so
 * returning a positive number for "missing" on the left side puts those
 * rows at the bottom ascending and at the top descending — which would
 * misplace empties. We sidestep by short-circuiting equal cases first
 * and falling back to localeCompare for everything else.
 */
function compareStrings(a: string | undefined, b: string | undefined): number {
  const aEmpty = !a;
  const bEmpty = !b;
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  return a.localeCompare(b);
}

function OrdersEmptyState() {
  return (
    <div className="border-border bg-card flex flex-col items-center justify-center rounded-md border border-dashed px-6 py-16 text-center">
      <div className="bg-muted text-muted-foreground mb-4 flex size-12 items-center justify-center rounded-full">
        <Package className="size-6" aria-hidden="true" />
      </div>
      <p className="text-foreground text-sm font-medium">No orders yet</p>
      <p className="text-muted-foreground mt-1 max-w-sm text-sm">
        Once your store is connected and customers place orders, they&apos;ll appear here ready to
        ship.
      </p>
    </div>
  );
}

function NoMatchingOrders({ onClear }: { onClear: () => void }) {
  return (
    <div className="border-border bg-card flex flex-col items-center justify-center rounded-md border border-dashed px-6 py-12 text-center">
      <p className="text-foreground text-sm font-medium">No orders match your filters</p>
      <p className="text-muted-foreground mt-1 max-w-sm text-sm">
        Try a different search term or status.
      </p>
      <Button type="button" variant="outline" size="sm" className="mt-4" onClick={onClear}>
        Clear filters
      </Button>
    </div>
  );
}

function SyncNowButton({
  connection,
  disabled,
  onJobStarted,
}: {
  connection: BcConnection;
  disabled: boolean;
  onJobStarted: (jobId: string) => void;
}) {
  const [starting, setStarting] = useState(false);

  async function handleClick() {
    setStarting(true);
    const result = await syncStoreOrders(connection.connection_id);
    setStarting(false);
    if (result.isErr()) {
      toast.error("We couldn't start the sync. Please try again.");
      return;
    }
    onJobStarted(result.value.job_id);
  }

  return (
    <Button
      type="button"
      onClick={() => void handleClick()}
      disabled={disabled || starting || connection.status !== 'active'}
    >
      {starting ? 'Starting…' : 'Sync now'}
    </Button>
  );
}

/**
 * Map a BC `status_id` to a status-pill tone. Ship-ready and other
 * "needs attention" statuses get warning amber; shipped / completed
 * get success green; cancelled / refunded / declined stay muted.
 */
export function toneForBcStatus(statusId: number): StatusTone {
  // 7 Awaiting Payment, 8 Awaiting Pickup, 9 Awaiting Shipment,
  // 11 Awaiting Fulfillment, 12 Manual Verification Required
  if ([7, 8, 9, 11, 12].includes(statusId)) return 'warning';
  // 2 Shipped, 3 Partially Shipped, 10 Completed
  if ([2, 3, 10].includes(statusId)) return 'success';
  return 'muted';
}

/** Stripe-style money display: known-currency symbol, else the code. */
export function formatMoney(currency: string, amount: string): string {
  if (currency === 'GBP') return `£${amount}`;
  if (currency === 'USD') return `$${amount}`;
  if (currency === 'EUR') return `€${amount}`;
  return `${amount} ${currency}`;
}

/**
 * Display name for an order's source channel. Param is widened to
 * `string` so future channels (per ADR 0016) round-trip through the
 * fallback without a type bump; the known set maps to nicely-cased
 * display names.
 */
export function channelLabel(source: string): string {
  if (source === 'bigcommerce') return 'BigCommerce';
  return source;
}

export function filterOrders(
  orders: readonly Order[],
  search: string,
  statusFilter: StatusFilter,
): readonly Order[] {
  const needle = search.trim().toLowerCase();
  return orders.filter((order) => {
    if (!matchesStatusFilter(order.bc_status_id, statusFilter)) return false;
    if (needle === '') return true;
    return (
      String(order.bc_order_id).includes(needle) ||
      (order.customer_name?.toLowerCase().includes(needle) ?? false) ||
      (order.customer_email?.toLowerCase().includes(needle) ?? false) ||
      order.bc_status.toLowerCase().includes(needle)
    );
  });
}

function matchesStatusFilter(statusId: number, filter: StatusFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'waiting') return WAITING_STATUS_IDS.has(statusId);
  if (filter === 'shipped') return SHIPPED_STATUS_IDS.has(statusId);
  return CANCELLED_STATUS_IDS.has(statusId);
}
